import { useEffect, useState } from 'react';
import { Modal, Button, Form, Card, ModalHeader } from 'react-bootstrap';
import Stack from 'react-bootstrap/Stack';
import api from '../../../utils/axios';
import { toDE, toYMD } from '../../../utils/utils'
import EditIcon from '../../icons/EditIcon';
// import DeleteIcon from '../../icons/DeleteIcon';
import LinkBox from '../../icons/LinkBox';
import { useReferenceData } from '../../../hooks/useReferenceData';
import { useAuth } from '../../../contexts/AuthContext';

// import type { ClientLawPart } from '../LawManagementPanel';

//#region Type definitions

type ClientLawPart = {
  id: number;
  law_part_id: number;
  client_id: number;
  client: string;
  version: number;
  comments: string;
  notes: string;
  cluster_ids: number[] | null;
};

type LawPart = {
  id: number;
  law_id: number;
  law_part_id: number;
  version: number;
  paragraphs: string;
  regulation: string;
  comments: string;
  client_versions: ClientLawPart[];
};

type LegalArea = {
  id: number;
  name: string;
  color: string;
  selected: boolean;
};

type Cluster = {
  id: number;
  name: string;
  color: string;
  selected: boolean;
};

type User = {
  id: number;
  username: string;
  email: string;
  role: string;
};
// type Client = { id: number; name: string };

type Law = {
  id: number;
  law_id: number;
  name: string;
  abbr: string;
  version: number;
  current?: boolean;
  last_change_date: string | null;
  last_change: string;
  link1?: string;
  link2?: string;
  amendment_date: string | null;
  amendment_comment: string;
  last_check_date?: string;
  last_check_user?: number;
  legal_areas: LegalArea[];
  parts: LawPart[];
};

type Props = {
  show: boolean;
  law: Law;
  onClose: () => void;
  onSave: () => void;
};

const LawModal = ({ show, law, onClose, onSave }: Props) => {
  const [editableLaw, setEditableLaw] = useState({ ...law });
  const [editingLaw, setEditingLaw] = useState(false);
  const [editingParts, setEditingParts] = useState<{ [key: number]: boolean }>({});
  const [editingClientParts, setEditingClientParts] = useState<{ [key: number]: boolean }>({});
  const [editingClientPartIdForClusters, setEditingClientPartIdForClusters] = useState<number | null>(null);
  const [editingClientPartForClusters, setEditingClientPartForClusters] = useState<ClientLawPart | null>(null);
  const [selectedLegalAreaIds, setSelectedLegalAreaIds] = useState<number[]>(
    (editableLaw.legal_areas ?? []).map(la => la.id)
  );
  const [lawValidationError, setLawValidationError] = useState<string>("");
  const [selectedClusterIds, setSelectedClusterIds] = useState<number[]>([]);
  const [clusterValidationError, setClusterValidationError] = useState<string>("");

  const [showClusterSelectorModal, setShowClusterSelectorModal] = useState(false);
  const { legalAreas, clusters, users } = useReferenceData() as {
  //   clients: Client[];
    legalAreas: LegalArea[];
    clusters: Cluster[];
    users: User[];
  };
  // const { isAuthenticated, user, logout } = useAuth();
  const { user } = useAuth();
  const [showLegalAreasModal, setShowLegalAreasModal] = useState(false);
  const [expandClients, setExpandClients] = useState(false);
  const [showClients, setShowClients] = useState(true);

  useEffect(() => {
    setEditableLaw({ ...law });
    setLawValidationError("");
  }, [law, show]);

  useEffect(() => {
    // When legal areas change, update selected IDs
    setSelectedLegalAreaIds((editableLaw.legal_areas ?? []).map(la => la.id));
  }, [editableLaw.legal_areas]);

  interface LawChangeEvent extends React.ChangeEvent<HTMLInputElement> {}
  interface HandlePartChange {
      (id: number, field: keyof LawPart, value: any): void;
  }

  interface handleClientPartChange {
      (id: number, field: keyof ClientLawPart, value: any): void;
  }

  const isValidDate = (value: string) => {
    if (value) {
      const isValidDateStr = /^\d{4}-\d{2}-\d{2}$/.test(value);
      const year = value.split('-')[0];
      const yearIsValid = year.length === 4 && parseInt(year) >= 1900 && parseInt(year) <= 2100;
      return isValidDateStr && yearIsValid;
    }
    return false;
  }

  const handleLawChange = (e: LawChangeEvent) => {
      const { name, value, type, checked } = e.target;
      // const val = type === 'checkbox' ? checked : value;
      setEditableLaw(prev => ({ 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value,
      }));
  };

  const handleDateChange = 
    (fieldName: 'last_change_date' | 'amendment_date') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (!value) {
        setEditableLaw(prev => ({ ...prev, [fieldName]: null }));
        return;
      }
      if (isValidDate(value) || value.length <= 10) {
        setEditableLaw(prev => ({ ...prev, [fieldName]: value }));
      }
    };

  const handleExpandClients = () => {
    setExpandClients(!expandClients);
  };

  const handleShowClients = () => {
    const nextShow = !showClients;
    setShowClients(nextShow);
    if (!nextShow) {
      setExpandClients(false);
    }
  };

  const handleLegalAreaModalClose = () => {
    // Validate at least one legal area selected
    if (selectedLegalAreaIds.length === 0) {
      setLawValidationError("Bitte wählen Sie mindestens ein Rechtsgebiet aus.");
      return;
    }

    // Update editableLaw with selected legal areas
    const updatedLegalAreas = (legalAreas ?? []).filter(la =>
      selectedLegalAreaIds.includes(la.id)
    ); // get full objects - this is working
    setEditableLaw(prev => ({ ...prev, legal_areas: updatedLegalAreas }));
    // console.log({updatedLegalAreas});
    // console.log({editableLaw});
    setShowLegalAreasModal(false);
  }

  const handlePartChange: HandlePartChange = (id, field, value) => {
    setEditableLaw({
        ...editableLaw,
        parts: editableLaw.parts.map((p: LawPart) =>
            p.law_part_id === id ? { ...p, [field]: value } : p
        )
    });
  };

  const handleClientPartChange: handleClientPartChange = (id, field, value) => {
    setEditableLaw({
      ...editableLaw,
      parts: editableLaw.parts.map(p => ({
        ...p,
        client_versions: p.client_versions?.map(c =>
          c.id === id ? { ...c, [field]: value } : c
        )
      }))
    });
  };

  const handleShowClusterSelectorModal = (clp: ClientLawPart) => {
    // Pre-select clusters based on the client's current clusters
    setEditingClientPartIdForClusters(clp.id);
    setEditingClientPartForClusters(clp);

    // const currentIds = (clp.cluster_ids ?? []).map(c => c.id);
    setSelectedClusterIds(clp.cluster_ids ? clp.cluster_ids : []);

    setClusterValidationError("");
    setShowClusterSelectorModal(true);
  }

  const handleAddLawPart = () => {
    const newId = -1;
    const newLawPart: LawPart = {
      id: -1,
      law_id: editableLaw.law_id,
      law_part_id: newId,
      version: 1,
      paragraphs: '',
      regulation: '',
      comments: '',
      client_versions: [],
    };

    const updatedParts = [...editableLaw.parts, newLawPart];
    setEditableLaw({ ...editableLaw, parts: updatedParts });
    setEditingParts(prev => ({ ...prev, [newId]: true })); // immediately editable
  };

//#region Delete functions

  // const deleteLaw = async (law_id: number) => {
  //   if (!window.confirm('Sind Sie sicher, dass Sie diese Vorschrift löschen möchten? Dies betrifft alle Kunden!')) return;
  //   await api.delete<Law>('/admin/laws/' + law_id)
  //     .then(() => {
  //       onClose();
  //       onSave();
  //     })
  //     .catch(err => {
  //       console.error('Failed to delete law', err);
  //       alert('Fehler beim Löschen der Vorschrift.');
  //     });
  //   };
    
//#region Save functions

  const saveLaw = async () => {
    const payload = {
      ...editableLaw,
      amendment_date: toYMD(editableLaw.amendment_date) || null,
      last_change_date: toYMD(editableLaw.last_change_date) || null,
    };

    if (selectedLegalAreaIds.length === 0) {
      setLawValidationError("Bitte wählen Sie mindestens ein Rechtsgebiet aus.");
      return;
    }
    const { data }  = await api.post<Law>(`/admin/laws`, payload);
  
    setEditableLaw(prev => ({
      ...prev,
      id: data.id,
      // prefer the server's version if it returns one; otherwise increment
      version: typeof data.version === 'number' ? data.version : (prev.version ?? 0) + 1,
      amendment_date: payload.amendment_date,
      last_change_date: payload.last_change_date,
    }));

    setEditingLaw(false);
    onSave();
  };

  const saveCheck = async () => {
    const currentUser = users.find(u => u.username === user?.username);
    const payload = {
      ...editableLaw,
      last_check_date: new Date().toISOString().split('T')[0],
      last_check_user: currentUser?.id,
    };
    await api.put<Law>(`/admin/law`, payload);
    setEditableLaw(prev => ({ 
      ...prev, 
      last_check_date: payload.last_check_date, 
      last_check_user: payload.last_check_user,
      version: typeof prev.version === 'number' ? prev.version + 1 : 1, // increment version to trigger re-render
    }));
    onSave();
  };

  const saveLawPart = async (law_part_id: number) => {
    const part = editableLaw.parts.find(p => p.law_part_id === law_part_id);
    if (!part) return;

    const payload = {
      ...part,
    };

    const { data } = await api.post<LawPart>(`/admin/law_parts`, payload);

    setEditableLaw(prev => {
      const parts = prev.parts.map(p => {
        if (p.law_part_id !== law_part_id) return p;
        return {
          ...p,
          ...data,
          version: typeof data.version === 'number' ? data.version : (prev.version ?? 0) + 1,
        };
      });
  // console.log({editableLaw});
      return { ...prev, parts };
    });

    // If the server changed the canonical key, migrate the editing map key
    setEditingParts(prev => {
      const newKey = data.law_part_id ?? law_part_id;
      const next = { ...prev, [newKey]: false };
      if (newKey !== law_part_id) delete next[law_part_id];
      return next;
    });

    onSave();
  };

  const saveClientLawPart = async (id: number) => {
    const part = editableLaw.parts.find(p => p.client_versions?.some(c => c.id === id));
    const clientPart = part?.client_versions?.find(c => c.id === id);
    if (!clientPart || !part) return;
    clientPart.law_part_id = part.law_part_id;

    const { data } = await api.post<ClientLawPart>(`/admin/client_law_parts`, clientPart);

    setEditableLaw(prev => ({
      ...prev,
      parts: prev.parts.map(p => {
        if (!p.client_versions?.some(c => c.id === id)) return p;
        const client_versions = p.client_versions.map(c => {
          if (c.id !== id) return c;
          return { ...c, ...data };
        });
        return { ...p, client_versions };
      }),
    }));

    setEditingClientParts(prev => {
      const newId = data.id ?? id;
      const next = { ...prev, [newId]: false };
      if (newId !== id) delete next[id];
      return next;
    });

    console.log("Saved cluster_ids:", clientPart?.cluster_ids);
    saveClientPartClusters(clientPart.client_id, part.law_part_id, clientPart?.cluster_ids ?? []);

    onSave();
  };

  const applySelectedClustersToEditingClp = (nextSelectedIds: number[]) => {
    // if (editingClientPartIdForClusters == null) return;
    if (editingClientPartForClusters == null) return;

    const nextClusters = (clusters ?? []).filter(cl => nextSelectedIds.includes(cl.id));

    setEditableLaw(prev => ({
      ...prev,
      parts: prev.parts.map(p => ({
        ...p,
        client_versions: (p.client_versions ?? []).map(cvp =>
          cvp.id === editingClientPartIdForClusters
            ? { ...cvp, clusters: nextClusters, cluster_ids: nextSelectedIds }
            : cvp
        ),
      })),
    }));

    console.log({ nextSelectedIds, nextClusters });
    console.log({ editableLaw });
    console.log({ editingClientPartForClusters });
  };

  const handleClusterSelectorModalClose = () => {
    setShowClusterSelectorModal(false);
    setEditingClientPartIdForClusters(null);
    setEditingClientPartForClusters(null);
  };

  const saveClientPartClusters = async (client_id: number, law_part_id: number, clusterIds: number[]) => {
    // API call to save clusters for the client law part
    await api.post(`/admin/client_law_parts/${client_id}/${law_part_id}/clusters`, { cluster_ids: clusterIds });

    // Update local state to reflect changes immediately
    const updatedClusters = (clusters ?? []).filter(c => clusterIds.includes(c.id));

    setEditableLaw(prev => ({
      ...prev,
      parts: prev.parts.map(p => ({
        ...p,
        client_versions: (p.client_versions ?? []).map(cvp =>
          cvp.id === editingClientPartIdForClusters
            ? { ...cvp, clusters: updatedClusters, cluster_ids: clusterIds }   // <-- THIS is the key line
            : cvp
        ),
      })),
    }));
    setClusterValidationError("");
    setShowClusterSelectorModal(false);
  }
//#region Modal

  return (
    <Modal show={show} onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Vorschrift #{editableLaw.law_id} <small>(v{editableLaw.version})</small></Modal.Title>
      </Modal.Header>
      <Modal.Body className='edit_law'>
        <Card className="mb-3">
          <Card.Header>
            <Stack direction="horizontal" gap={0}>
              <div className="p-2">Details</div>
              <div className="p-2 ms-auto">
                <Button variant="outline-primary" size="sm" onClick={handleAddLawPart}>
                  + Neuen Vorschriftsteil hinzufügen
                </Button>
              </div>
              <div className="p-2">
                <span className='action-btn' onClick={() => setEditingLaw(p => !p)}>
                  <EditIcon />
                </span>
              </div>
            </Stack>
          </Card.Header>
{/*
//#region Law
*/}
          <Card.Body>
            {editingLaw ? (
              <>
                <Form.Group className="mb-2">
                  <Form.Label>Vorschrift</Form.Label>
                  <Form.Control name="name" value={editableLaw.name} onChange={handleLawChange} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>In Kraft?</Form.Label>
                  <Form.Check id='current' className='check' type='checkbox' name="current" checked={!!editableLaw.current} onChange={handleLawChange} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Abkürzung Vorschrift</Form.Label>
                  <Form.Control name="abbr" value={editableLaw.abbr} onChange={handleLawChange} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Datum letzte Änderung</Form.Label>
                  <Form.Control 
                    type="date" 
                    name="last_change_date" 
                    value={toYMD(editableLaw.last_change_date)} 
                    onChange={handleDateChange('last_change_date')}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      const value = e.target.value;
                      if (value && !isValidDate(value)) {
                        setEditableLaw(prev => ({ ...prev, last_change_date: null }));
                      }
                    }}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Gesetzblätter</Form.Label>
                  <Form.Control as="textarea" name="last_change" value={editableLaw.last_change} onChange={handleLawChange} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Link 1</Form.Label>
                  <Form.Control name="link1" value={editableLaw.link1} onChange={handleLawChange} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Link 2</Form.Label>
                  <Form.Control name="link2" value={editableLaw.link2} onChange={handleLawChange} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Datum des Inkrafttretens der Änderung</Form.Label>    
                  <Form.Control 
                    name="amendment_date" 
                    type="date" 
                    value={toYMD(editableLaw.amendment_date)}
                    onChange={handleDateChange('amendment_date')}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      const value = e.target.value;
                      if (value && !isValidDate(value)) {
                        setEditableLaw(prev => ({ ...prev, amendment_date: null }));
                      }
                    }}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Kurzbeschreibung der Änderung</Form.Label>
                  <Form.Control name="amendment_comment" as="textarea" value={editableLaw.amendment_comment}
                    onChange={handleLawChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>
                    <Button className='action-btn' size="sm" onClick={() => setShowLegalAreasModal(true)}>
                      <EditIcon />
                    </Button>
                    Rechtsgebiete (mind. 1 erforderlich):</Form.Label>
                    {(editableLaw.legal_areas ?? []).map(la => (
                      <div className='legal-area' style={{backgroundColor: la.color}} key={la.id}>{la.name}</div>
                    ))}
                </Form.Group>
                <div className="button-div">
                  <Button variant="primary" onClick={saveLaw}>Vorschrift speichern</Button>
                  {!!lawValidationError && (
                    <div className="text-danger mt-2">{lawValidationError}</div>
                  )}
                  {/* {editableLaw.id && (
                    <span onClick={() => deleteLaw(editableLaw.law_id)}><DeleteIcon/></span>
                  )} */}
                </div>
              </>
            ) : (
              <>
                <p><strong>Bezeichnung:</strong> {editableLaw.name}</p>
                <p><strong>In Kraft:</strong> {editableLaw.current ? 'Ja' : 'Nein'}</p>
                <p><strong>Vorschrift Abkürzung:</strong> {editableLaw.abbr}</p>
                <p><strong>Datum letzte Änderung:</strong> {toDE(editableLaw.last_change_date)}</p>
                <p><strong>Gesetzblätter:</strong> {editableLaw.last_change}</p>
                <p><strong>Link 1:</strong> {editableLaw.link1} <a href={editableLaw.link1} target='_blank'><LinkBox /></a></p>
                <p><strong>Link 2:</strong> {editableLaw.link2} <a href={editableLaw.link2} target='_blank'><LinkBox /></a></p>
                <p><strong>Datum des Inkrafttretens der Änderung:</strong> {toDE(editableLaw.amendment_date)}</p>
                <p><strong>Kurzbeschreibung der Änderung:</strong> {editableLaw.amendment_comment}</p>
                <p><strong>Rechtsgebiete:</strong></p>
                <Stack direction="vertical" gap={2} className="mb-2">
                  {(editableLaw.legal_areas ?? []).map(la => (
                    <div className='legal-area' style={{backgroundColor: la.color}} key={la.id}>{la.name}</div>
                  ))}
                </Stack>
                <Stack direction="horizontal" gap={0}>
                  <div><strong>Letzte Überprüfung:</strong> {editableLaw.last_check_date ? toDE(editableLaw.last_check_date) : 'nie'} 
                    {editableLaw.last_check_date ? ' durch ' + users.find(u => u.id === editableLaw.last_check_user)?.username : ''}
                  </div>
                  {user?.role === 'lawyer' ? (<div className="ms-auto"><Button variant="primary" onClick={saveCheck}>Überprüft</Button></div>) : (<div className="ms-auto" />)}
                </Stack>
              </>
            )}
          </Card.Body>
        </Card>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Form.Switch
            id='showClientsSwitch'
            label="Kunden anzeigen"
            checked={showClients}
            onChange={handleShowClients}
          />
          {showClients && (
            <Form.Switch
              id='showExpandClientsSwitch'
              key={'expand_clients_switch'}
              label="Kundenzuordnungsdetails anzeigen"
              checked={expandClients}
              onChange={handleExpandClients}
            />
          )}
        </div>
{/*
//#region LawPart
*/}
        {editableLaw.parts?.map(part => (
          <Card key={part.law_part_id} className="mb-3">
            <Card.Header>
              <Stack direction="horizontal" gap={0}>
                <div className="p-2" onClick={() => console.log(part)}>Vorschriftsteil #{part.law_part_id} <small>(v{part.version})</small></div>
                <div className="p-2 ms-auto">
                  {/* <Button variant="outline-primary" size="sm" onClick={() => handleAddClientLawPart(part.law_part_id)}>
                    + Neuen Kunden hinzufügen
                  </Button> */}
                </div>
                <div className="p-2">
                  <span className='action-btn' onClick={() => setEditingParts(prev => ({ ...prev, [part.law_part_id]: !prev[part.law_part_id] }))}>
                    <EditIcon />
                  </span>
                </div>
              </Stack>
            </Card.Header>
            <Card.Body>
              {editingParts[part.law_part_id] ? (
                <>
                  <Form.Group className="mb-2">
                    <Form.Label>Paragraf</Form.Label>
                    <Form.Control as="textarea" value={part.paragraphs}
                      onChange={e => handlePartChange(part.law_part_id, 'paragraphs', e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Inhalt</Form.Label>
                    <Form.Control as="textarea" value={part.regulation}
                      onChange={e => handlePartChange(part.law_part_id, 'regulation', e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Anmerkungen</Form.Label>
                    <Form.Control as="textarea" value={part.comments}
                      onChange={e => handlePartChange(part.law_part_id, 'comments', e.target.value)}
                    />
                  </Form.Group>
                  <div className="button-div">
                    <Button variant="primary" onClick={() => saveLawPart(part.law_part_id)}>
                      Vorschriftsteil speichern
                    </Button>
                    {/* <span onClick={() => setEditingLaw(p => !p)}><DeleteIcon/></span> */}
                  </div>
                </>
              ) : (
                <>
                  <p><strong>Paragraf:</strong> {part.paragraphs}</p>
                  <p><strong>Inhalt:</strong> {part.regulation}</p>
                  <p><strong>Anmerkungen:</strong> {part.comments}</p>
                </>
              )}
{/*
//#endregion
//#region ClientLawPart
*/}
              {showClients && part.client_versions?.map(clp => (
                <Card key={clp.id} className="mt-2">
                  <Card.Header>
                    <Stack direction="horizontal" gap={0}>
                      <div className="p-2">Kunde <strong>{clp.client}</strong> ({clp.client_id})</div>
                      <div className="p-2 ms-auto">
                        <span className='action-btn' id={`edit-client-part-${clp.id}`} onClick={() => 
                          setEditingClientParts(prev => ({ ...prev, [clp.id]: !prev[clp.id] }))}>
                          <EditIcon />
                        </span>
                     </div>
                    </Stack>
                  </Card.Header>
                  {expandClients && (
                  <Card.Body>
                    {editingClientParts[clp.id] ? (
                      <>
                        <Form.Group className="mb-2">
                          <Form.Label>Interne Anmerkungen</Form.Label>
                          <Form.Control as="textarea" value={clp.comments}
                            onChange={e => handleClientPartChange(clp.id, 'comments', e.target.value)}
                          />
                        </Form.Group>
                        {/* TODO: Maßnahmenliste implementieren */}
                        <Form.Group className="mb-2">
                          <Form.Label>Kundennotizen</Form.Label>
                          <Form.Control as="textarea" value={clp.notes}
                            onChange={e => handleClientPartChange(clp.id, 'notes', e.target.value)}
                          />
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label>Cluster</Form.Label>
                          <span className='action-btn' onClick={() => handleShowClusterSelectorModal(clp)}>
                            <EditIcon />
                          </span>
                          <div onClick={() => console.log(clp.cluster_ids)}>
                          {clp.cluster_ids?.map((c) => {
                            const cluster = clusters.find((ac) => ac.id === c);
                            if (!cluster) return;
                            return (
                              <span 
                                key={cluster.id}
                                className={`cluster cluster-${c}`}
                                style={{backgroundColor: cluster.color}}
                              >
                              {cluster.name}
                            </span>
                          );}) || <em>-</em>}
                          </div>
                        </Form.Group>
                        <div className="button-div">
                          <Button variant="primary" onClick={() => saveClientLawPart(clp.id)}>
                            Speichern
                          </Button>
                          {/* <span onClick={() => setEditingLaw(p => !p)}><DeleteIcon/></span> */}
                        </div>
                      </>
                    ) : (
                      <>
                        <p><strong>Interne Anmerkungen:</strong> {clp.comments}</p>
                        <p><strong>Kundennotizen:</strong> {clp.notes}</p>
                        {/* TODO: Maßnahmenliste implementieren */}
                        <p><strong>Cluster:</strong></p>
                        <div onClick={() => console.log(clp.cluster_ids, clusters)}>
                          {clp.cluster_ids?.map((c) => {
                            const cluster = clusters.find((ac) => ac.id === c);
                            if (!cluster) return;
                            return (
                              <span 
                                key={cluster.id}
                                className={`cluster cluster-${c}`}
                                style={{backgroundColor: cluster.color}}
                              >
                              {cluster.name}
                            </span>
                          );}) || <em>-</em>}
                        </div>
                      </>
                    )}
                  </Card.Body>
                  )}
                </Card>
              ))}
            </Card.Body>
          </Card>
        ))}
      </Modal.Body>
      {/* <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
        <Button variant="primary" onClick={saveAll}>Save All</Button>
      </Modal.Footer> */}
{/*
//#endregion
//#region LegalAreaSelector Modal
*/}
      <Modal show={showLegalAreasModal} onHide={() => handleLegalAreaModalClose()} size="xl">
{/* ========================================================== */}
<ModalHeader closeButton>
  <Modal.Title>Rechtsgebiete für Vorschrift #{editableLaw.law_id} auswählen</Modal.Title>
</ModalHeader>
{/* ========================================================== */}
{/* ============================================================ */}  
<Modal.Body>
<Form.Group className="mb-2">
  <Form.Label>(mind. 1 erforderlich)</Form.Label>

  {/* <div className="d-flex flex-wrap gap-2"> */}
  <div className="legal-areas-list">
    {(legalAreas ?? []).map(la => {
      const checked = selectedLegalAreaIds.includes(la.id);
      return (
        <Form.Check
          key={la.id}
          type="checkbox"
          id={`legal-area-${la.id}`}
          label={
            <span className="legal-area" style={{ backgroundColor: la.color, padding: "2px 8px", borderRadius: 6 }}>
              {la.name}
            </span>
          }
          checked={checked}
          onChange={(e) => {
            setLawValidationError("");
            setSelectedLegalAreaIds(prev =>
              e.target.checked ? [...prev, la.id] : prev.filter(id => id !== la.id)
            );
          }}
        />
      );
    })}
  </div>

  {!!lawValidationError && (
    <div className="text-danger mt-2">{lawValidationError}</div>
  )}
</Form.Group>
</Modal.Body>
{/* ============================================================ */}
      </Modal>
{/*
//#region ClusterSelector Modal
*/}
      <Modal show={showClusterSelectorModal} onHide={() => handleClusterSelectorModalClose()} size="xl">
{/* ========================================================== */}
<ModalHeader closeButton>
  <Modal.Title>Cluster auswählen</Modal.Title>
</ModalHeader>
<Modal.Body>
<Form.Group className="mb-2">
  {/* <Form.Label>(mind. 1 erforderlich)</Form.Label> */}

  {/* <div className="d-flex flex-wrap gap-2"> */}
  <div className="legal-areas-list">
    {(clusters ?? []).map(cl => {
      const checked = selectedClusterIds.includes(cl.id);
      return (
        <Form.Check
          key={cl.id}
          type="checkbox"
          // id={`cluster-${cl.id}`}
          label={
            <span className={`cluster cluster-${cl.id}`} style={{ backgroundColor: cl.color, padding: "2px 8px", borderRadius: 6 }}>
              {cl.name} {cl.id}
            </span>
          }
          checked={checked}
          onChange={(e) => {
            setClusterValidationError("");

            setSelectedClusterIds(prev => {
              const next = e.target.checked
                ? [...prev, cl.id]
                : prev.filter(id => id !== cl.id);

              applySelectedClustersToEditingClp(next);   // ✅ instant UI update
              return next;
            });
          }}
        />
      );
    })}
  </div>

  {!!clusterValidationError && (
    <div className="text-danger mt-2">{clusterValidationError}</div>
  )}
</Form.Group>
</Modal.Body>
{/* ============================================================ */}
      </Modal>
    </Modal>
  );
};

export default LawModal;
